import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';

import { Container, SimpleButton, ReportScoringBandsLegend, Loading } from '@/src/ui';
import { apiRequest } from '@/src/services';
import ClassSummaryFilters from './components/ClassSummaryFilters';
import ClassSummaryTable from './components/ClassSummaryTable';
import type { ClassSummaryResultDTO } from './models/classSummaryDTOs';
import { useAuth } from '@/src/app/context/useAuth';

export interface FilterState {
    gradeLevelMasterId: number;
    districtId: number;
    schoolId: number;
    classId: number;
    examTypeMasterId: number;
    examId: number;
}

const ClassSummaryPage: React.FC = () => {
    document.title = 'Class Summary';

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const [showReportingBands, setShowReportingBands] = useState(false);

    const [filterState, setFilterState] = useState<FilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    classId: parsed.classId || 0,
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examId: parsed.examId || 0,
                    viewMode: parsed.viewMode || 1,
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            gradeLevelMasterId: 0,
            districtId: 0,
            schoolId: 0,
            classId: 0,
            examTypeMasterId: 0,
            examId: 0,
            viewMode: 1,
        };
    });

    useEffect(() => {
        if (!userId) return;

        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examTypeMasterId: filterState.examTypeMasterId,
                examId: filterState.examId,
            };

            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    const [tableData, setTableData] = useState<ClassSummaryResultDTO[]>([]);

    const refreshTableData = useCallback((state: FilterState) => {
        setError('');
        setLoaded(false);
        setHasSearched(true);
        const endpoint = `reports/classSummary?districtId=${state.districtId}&schoolId=${state.schoolId}&classId=${state.classId}&gradeId=${state.gradeLevelMasterId}&examTypeMasterId=${state.examTypeMasterId}&examId=${state.examId || 0}`;
        apiRequest<ClassSummaryResultDTO[]>('GET', endpoint)
            .then((res) => {
                if (Array.isArray(res.data)) {
                    setTableData(res.data);
                } else if (res.data && Array.isArray((res.data as { data: unknown }).data)) {
                    setTableData((res.data as { data: ClassSummaryResultDTO[] }).data);
                } else {
                    setTableData([]);
                }
            })
            .catch(() => {
                setError('Failed to load Class Summary Report');
                setTableData([]);
            })
            .finally(() => {
                setLoaded(true);
            });
    }, []);

    useEffect(() => {
        const fs = filterState;
        if (fs.districtId && fs.schoolId && fs.gradeLevelMasterId && fs.examId) {
            refreshTableData(fs);
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="page-header">Class Summary Report</h1>
            <br />

            <ClassSummaryFilters
                refreshTableData={refreshTableData}
                filterState={filterState}
                setFilterState={setFilterState}
            />

            <Loading visible={hasSearched && !loaded} message="Loading Class Summary Report..." />

            <Box sx={{ pb: 2, display: 'flex', justifyContent: 'start' }}>
                <SimpleButton
                    variant="outlined"
                    onClick={() => setShowReportingBands(!showReportingBands)}
                    aria-expanded={showReportingBands}
                    aria-label="Toggle reporting bands dropdown"
                >
                    {showReportingBands ? 'Hide Scoring Bands' : 'Show Scoring Bands'}
                </SimpleButton>
                {filterState.examId !== null && filterState.examId !== 0 && (
                    <div className="ml-auto">
                        <Button
                            onClick={() => null}
                            sx={{
                                textTransform: 'none',
                            }}
                            variant="outlined"
                            href={`/reports/questionList/${filterState.examId}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            View Assessment
                        </Button>
                    </div>
                )}
            </Box>

            <Box sx={{ mb: showReportingBands ? 2 : 0 }}>
                <ReportScoringBandsLegend dataLoaded={loaded} showLegend={showReportingBands} />
            </Box>

            <ClassSummaryTable
                data={loaded ? tableData : []}
                isLoading={false}
                error={error}
                emptyMessage={hasSearched && loaded ? 'No class summary data found.' : ''}
            />
        </Container>
    );
};

export default ClassSummaryPage;
